import type { SanityImage } from "@/types/sanity"
import { createClient } from "@sanity/client"
import imageUrlBuilder from "@sanity/image-url"
import type { ImageUrlBuilder } from "@sanity/image-url/lib/types/builder"

// Sanity client configuration
export const sanityClient = createClient({
  projectId: import.meta.env.VITE_SANITY_PROJECT_ID as string,
  dataset: import.meta.env.VITE_SANITY_DATASET as string,
  apiVersion: import.meta.env.VITE_SANITY_API_VERSION as string,
  useCdn: true, // Use CDN for better performance
  token: undefined, // No token needed for public data
  perspective: "published", // Only fetch published content
  stega: {
    enabled: false,
    studioUrl: "https://poremat.sanity.studio",
  },
})

// Image URL builder
const builder = imageUrlBuilder(sanityClient)

export function urlForImage(source: SanityImage): ImageUrlBuilder {
  return builder.image(source)
}

// Main image first, then the gallery images.
export function getProductImages(product: {
  mainImage?: SanityImage | null
  galleryImages?: SanityImage[] | null
}): SanityImage[] {
  return [product.mainImage, ...(product.galleryImages ?? [])].filter(
    (image): image is SanityImage => Boolean(image?.asset)
  )
}

// GROQ query helpers
export const groqLocalized = (field: string, locale: string): string => {
  return `${field}.${locale}`
}

// Common GROQ fragments
export const seoFragment = `
  seo {
    metaTitle,
    metaDescription,
    ogImage,
    noIndex
  }
`

export const imageFragment = `
  ...,
  asset->
`

export const productFragment = `
  _id,
  _type,
  title,
  slug,
  excerpt,
  description,
  keyFeaturesTitle,
  keyFeatures[] {
    feature,
    icon
  },
  applications,
  benefits[] {
    title,
    description
  },
  specs[] {
    key,
    value
  },
  mainImage {
    ${imageFragment}
  },
  galleryImages[] {
    ${imageFragment}
  },
  status,
  ctaSection {
    title,
    description,
    primaryButton,
    primaryButtonLink
  },
  downloads[] {
    title,
    file {
      asset->
    }
  },
  ${seoFragment}
`

export const resourceFragment = `
  _id,
  _type,
  title,
  slug,
  type,
  excerpt,
  content,
  publishedAt,
  coverImage {
    ${imageFragment}
  },
  relatedProducts[]-> {
    _id,
    title,
    slug
  },
  ${seoFragment}
`

export const pageHomeFragment = `
  _id,
  heroTitle,
  heroSubtitle,
  heroCta,
  heroVideo {
    asset->
  },
  heroPoster {
    ${imageFragment}
  },
  statistics[] {
    value,
    label,
    icon
  },
  features[] {
    title,
    description,
    icon,
    slug
  },
  sections[] {
    title,
    description,
    link
  },
  companySection {
    title,
    description,
    image {
      ${imageFragment}
    },
    buttonText
  },
  ctaSection {
    title,
    description,
    primaryButton,
    primaryButtonLink,
    secondaryButton,
    secondaryButtonLink
  },
  careerSection {
    eyebrow,
    title,
    description,
    image {
      ${imageFragment}
    },
    buttonText,
    buttonLink
  },
  seo
`

export const pageCompanyFragment = `
  heroTitle,
  heroSubtitle,
  mission,
  vision,
  values[] {
    title,
    description,
    icon
  },
  timeline[] {
    year,
    title,
    description
  },
  highlights[] {
    label,
    value
  },
  careerSection {
    eyebrow,
    title,
    description,
    image {
      ${imageFragment}
    },
    buttonText,
    buttonLink
  },
  logoMeaningSection {
    eyebrow,
    title,
    description,
    image {
      ${imageFragment}
    }
  }
`

export const pageApproachFragment = `
  heroTitle,
  heroSubtitle,
  features[] {
    slug,
    title,
    description,
    detailedContent,
    image {
      ${imageFragment}
    },
    benefits[] {
      title,
      description
    },
    metrics[] {
      label,
      value,
      icon
    }
  },
  ctaSection {
    title,
    description,
    primaryButton,
    primaryButtonLink,
    secondaryButton,
    secondaryButtonLink
  }
`

export const pageProductsFragment = `
  ctaSection {
    title,
    description,
    primaryButton,
    primaryButtonLink
  }
`

export const productsListFragment = `
  _id,
  title,
  slug,
  category,
  excerpt,
  status,
  mainImage {
    ${imageFragment}
  },
  galleryImages[] {
    ${imageFragment}
  }
`

export const resourcesListFragment = `
  _id,
  title,
  slug,
  type,
  excerpt,
  publishedAt
`
